import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import './SubscriptionCard.css';
import ManageSubscription from './ManageSubscription.jsx';
import {
	fetchCurrentSubscription,
	fetchAvailablePlans,
	upgradeSubscription,
	downgradeSubscription,
	pauseSubscription,
	resumeSubscription,
	cancelSubscription,
	updateSmsAddon,
} from 'data/reducers/subscriptionReducer';
import { useUpgradeIncentive } from 'hooks/useUpgradeIncentive';
import { openModal } from 'data/reducers/modalReducer';
import { MODAL_TYPES } from 'view/ModalManager';
import moment from 'moment';
import {
	SMS_ADDON_FLAG_KEY,
	reportsSmsAddonFlag,
	getSmsAddonPeriodCost,
	getSmsAddonPriceLabel,
	getSmsAddonPurchaseConsentMessage,
	getSmsAddonPurchaseConfirmLabel,
} from './smsAddon';

const SubscriptionCard = () => {
	const dispatch = useDispatch();

	const { showUpgradePlanIncentive } = useUpgradeIncentive();

	const {
		currentSubscription,
		isLoadingCurrentSubscription,
		currentSubscriptionError,
		availablePlans,
		isPerformingAction,
		isUpdatingSmsAddon,
	} = useSelector((state) => state.subscription);

	useEffect(() => {
		dispatch(fetchCurrentSubscription());
		dispatch(fetchAvailablePlans());
	}, [dispatch]);

	const handleUpgrade = (planId) => {
		dispatch(upgradeSubscription(planId, true));
	};

	const handleDowngrade = (planId) => {
		dispatch(downgradeSubscription(planId, false));
	};

	const handlePause = () => {
		dispatch(pauseSubscription());
	};

	const handleResume = () => {
		dispatch(resumeSubscription());
	};

	const handleCancel = (immediately = false, reason = '') => {
		dispatch(cancelSubscription(immediately, reason));
	};

	const handleAddSmsAddon = () => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: 'Add SMS Instant Alerts?',
				message: getSmsAddonPurchaseConsentMessage(isYearly),
				confirmLabel: getSmsAddonPurchaseConfirmLabel(isYearly),
				closeLabel: 'Cancel',
				color: 'success',
				onConfirm: () => {
					dispatch(updateSmsAddon(true));
				},
			}),
		);
	};

	const handleCancelSmsAddon = () => {
		dispatch(
			openModal(MODAL_TYPES.CONFIRM, {
				title: 'Cancel SMS Alerts Add-on?',
				message:
					'Are you sure you want to cancel the SMS Instant Alerts add-on? You will no longer receive real-time SMS notifications.',
				confirmLabel: 'Yes, Cancel Add-on',
				closeLabel: 'Keep Add-on',
				color: 'danger',
				onConfirm: () => {
					dispatch(updateSmsAddon(false));
				},
			}),
		);
	};

	if (isLoadingCurrentSubscription) {
		return (
			<div className="subscription-wrapper">
				<div className="text-center py-5">
					<div className="spinner-border" role="status">
						<span className="sr-only">Loading subscription details...</span>
					</div>
				</div>
			</div>
		);
	}

	if (currentSubscriptionError && false) {
		return (
			<div className="subscription-wrapper">
				<div className="alert alert-danger">
					Error loading subscription details: {currentSubscriptionError}
				</div>
			</div>
		);
	}

	if (!currentSubscription?.id) {
		return (
			<div className="subscription-wrapper">
				<div className="alert alert-info d-flex justify-content-between align-items-center">
					No active subscription found. Please subscribe to a plan to access
					premium features.
					<div className="">
						<button
							onClick={() => showUpgradePlanIncentive({ force: true })}
							className="btn btn-primary"
						>
							Upgrade Plan
						</button>
					</div>
				</div>
			</div>
		);
	}

	// Extract data from current subscription and plan
	const plan = currentSubscription?.plan;
	const subscription = currentSubscription?.subscription || currentSubscription;

	const currentPlan = plan?.name || 'Unknown Plan';
	const interval = currentSubscription?.interval || plan?.interval || 'monthly';
	const isYearly = interval === 'yearly';
	const billingType = isYearly ? 'Yearly' : 'Monthly';
	const periodLabel = isYearly ? '/yr' : '/mo';
	const price = plan?.price_dollars || 0;
	const currency = plan?.currency === 'USD' ? '$' : plan?.currency || '$';
	const activeUntil = subscription?.active_until || 'Unknown';
	const nextBillingDate = subscription?.next_billing_date || 'Unknown';
	const subscriptionStatus = subscription?.stripe_status || 'active';
	// Tri-state: the flag is absent, present and false, or present and true.
	// Absent means the backend does not report the add-on at all, so neither the
	// purchase surface nor the entitlement may be inferred from it.
	const backendReportsSmsAddon =
		reportsSmsAddonFlag(currentSubscription) ||
		reportsSmsAddonFlag(currentSubscription?.subscription);
	const hasSmsAddon =
		backendReportsSmsAddon &&
		Boolean(
			currentSubscription?.[SMS_ADDON_FLAG_KEY] ||
				subscription?.[SMS_ADDON_FLAG_KEY]
		);
	const smsCost = getSmsAddonPeriodCost(isYearly);
	const totalRecurring = (
		Number(price) + (hasSmsAddon ? smsCost : 0)
	).toFixed(2);

	return (
		<div className="subscription-wrapper">
			<div className="row">
				<div className="col-lg-8">
					<div className="subscription-card">
						<div className="subscription-header">
							<div className="subscription-left-header">
								<div className="subscription-plan-line">
									<div>
										<span className="subscription-plan-name">
											{currentPlan}
										</span>
										<span className="subscription-billing-type">
											{billingType}
										</span>
									</div>
								</div>
								<div className="d-flex align-items-center">
									<div className="subscription-price-section mr-5 ml-0">
										<span className="subscription-price">
											{currency}
											{hasSmsAddon ? totalRecurring : price}
											<span className="subscription-period">{periodLabel}</span>
										</span>
									</div>
									<div className="mx-5"></div>
								</div>

								{hasSmsAddon && (
									<div className="small text-muted mb-1">
										Base plan {currency}{price}{periodLabel} + SMS Add-on {getSmsAddonPriceLabel(isYearly)}
									</div>
								)}

								<div className="subscription-active-until">
									<span className="subscription-active-text">
										Active Until {activeUntil}
									</span>
								</div>

								{/* Status badge */}
								{subscriptionStatus && (
									<div className="subscription-status">
										<span
											className={`badge ${
												subscriptionStatus === 'active'
													? 'badge-success'
													: subscriptionStatus === 'cancelled_but_active'
														? 'badge-warning'
														: subscriptionStatus === 'cancelled'
															? 'badge-danger'
															: subscriptionStatus === 'trial'
																? 'badge-info'
																: 'badge-secondary'
											}`}
										>
											{subscriptionStatus === 'paused' ? (
												<span>
													Auto Cancels{' '}
													{moment(subscription.ends_at).format('MMM Do')}
												</span>
											) : (
												<span>
													{subscriptionStatus.charAt(0).toUpperCase() +
														subscriptionStatus.slice(1).replace('_', ' ')}
												</span>
											)}
										</span>
									</div>
								)}
							</div>

							<div className="subscription-right-header">
								<div className="subscription-next-billing">
									<div className="subscription-next-billing-title">
										{subscriptionStatus === 'active' && 'Next Billing Date'}
										{subscriptionStatus === 'trial' && 'Trial Ends'}
										{subscriptionStatus === 'paused' &&
											'Membership is valid until'}
										{subscriptionStatus === 'cancelled_but_active' &&
											'Subscription Ends'}
										{subscriptionStatus === 'cancelled' &&
											'Subscription Cancelled'}
									</div>

									<div className="subscription-next-billing-description">
										{subscriptionStatus === 'cancelled' && (
											<>No further charges will be made.</>
										)}

										{subscriptionStatus === 'active' && (
											<>
												{hasSmsAddon ? (
													<>
														You'll be charged {currency}
														{totalRecurring} for {currentPlan} Plan + SMS Add-on
													</>
												) : (
													<>
														You'll be charged {currency}
														{price} for the {currentPlan} Plan
													</>
												)}
											</>
										)}
										{subscriptionStatus === 'trial' && (
											<>No charges will be made until your trial ends.</>
										)}
										{subscriptionStatus === 'paused' && (
											<>
												No further charges will be made, until you resume your
												plan.
											</>
										)}
										{subscriptionStatus === 'cancelled_but_active' && (
											<>No further charges will be made.</>
										)}
									</div>
									<div className="subscription-billing-date">
										{nextBillingDate}
									</div>
								</div>
							</div>
						</div>

						{/* Subscription Add-ons Section - offered only when the backend
						    reports the add-on flag, so a backend that cannot sell the
						    add-on never shows a purchase button */}
						{backendReportsSmsAddon && (
							<div
								className="subscription-addons-section mt-4 pt-3 border-top"
								style={{ borderColor: 'rgba(0, 0, 0, 0.08)' }}
							>
								<div className="d-flex justify-content-between align-items-center mb-3">
									<h6 className="mb-0 font-weight-bold" style={{ fontSize: '0.95rem' }}>
										Active Add-ons (Recurring)
									</h6>
									{hasSmsAddon && (
										<span className="badge badge-success px-2 py-1" style={{ fontSize: '0.75rem' }}>
											✓ Recurring $19.99/mo Active
										</span>
									)}
								</div>
								<div
									className="subscription-addon-item d-flex justify-content-between align-items-center p-3 rounded"
									style={{
										background: hasSmsAddon ? 'rgba(34, 197, 94, 0.06)' : 'rgba(0, 0, 0, 0.02)',
										border: hasSmsAddon
											? '1px solid rgba(34, 197, 94, 0.35)'
											: '1px solid rgba(0, 0, 0, 0.08)',
									}}
								>
									<div className="d-flex align-items-center pr-3">
										<div className="mr-3" style={{ fontSize: '1.5rem', lineHeight: 1 }}>📱</div>
										<div>
											<div className="font-weight-bold" style={{ fontSize: '0.92rem', color: '#111827' }}>
												SMS Instant Alerts
											</div>
											<div className="text-muted small">
												Instant real-time SMS for Stop Work Orders, ECB/DOB hearings and critical deadlines ($19.99/month recurring)
											</div>
										</div>
									</div>
									<div className="flex-shrink-0 ml-2">
										{hasSmsAddon ? (
											<button
												className="btn btn-sm btn-outline-danger"
												disabled={isUpdatingSmsAddon}
												onClick={handleCancelSmsAddon}
											>
												{isUpdatingSmsAddon ? 'Updating...' : 'Cancel Add-on'}
											</button>
										) : (
											<button
												className="btn btn-sm btn-success"
												disabled={isUpdatingSmsAddon || subscriptionStatus !== 'active'}
												onClick={handleAddSmsAddon}
											>
												{isUpdatingSmsAddon ? 'Activating...' : 'Add (+$19.99/mo)'}
											</button>
										)}
									</div>
								</div>
							</div>
						)}
					</div>
				</div>

				<div className="col-lg-4">
					{!currentSubscription?.is_shared_account && (
						<ManageSubscription
							onUpgradePlan={handleUpgrade}
							onDowngradePlan={handleDowngrade}
							onPausePlan={handlePause}
							onCancelPlan={handleCancel}
							isLoading={isPerformingAction}
							availablePlans={availablePlans}
							currentPlan={plan}
							subscriptionStatus={subscriptionStatus}
							onResume={handleResume}
						/>
					)}

					{currentSubscription?.is_shared_account && (
						<div className="manage-subscription">
							<div className="manage-subscription-buttons">
								<div className="alert alert-info d-flex flex-column align-items-start">
									<h5>
										Subscribed By {currentSubscription?.account_owner?.name}
									</h5>
									<h6 className="mb-2">
										{currentSubscription?.account_owner?.email}
									</h6>
									<p className="mb-2">
										This account is part of a shared subscription managed by
										your portfolio owner. To make changes to your subscription
										plan, please contact your account administrator or support
										team.
									</p>
									<a
										className="btn btn-sm btn-primary"
										href={`mailto:${currentSubscription?.account_owner?.email}`}
									>
										Contact {currentSubscription?.account_owner?.name}
									</a>
								</div>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default SubscriptionCard;
